// src/components/UserLimitModal.tsx
import React, { useEffect, useState } from "react";
import { Modal, Button, Form, Spinner, Card, ProgressBar, Row, Col } from "react-bootstrap";
import { UserModel } from "../../models/UserModel";
import { toast } from "react-toastify";
import { FaRedo, FaChartBar, FaMoneyBillWave, FaExclamationTriangle } from "react-icons/fa";
import { getClientWithLimits } from "../../services/User_service";

interface UserLimitModalProps {
  isOpen: boolean;
  onClose: () => void;
  user: UserModel | null;
  onSave: (id: number, values: { unpaid_annonce_limit: number; unpaid_amount_limit: number }) => Promise<void>;
  onReset: (id: number) => Promise<void>;
}

export default function UserLimitModal({
  isOpen,
  onClose,
  user,
  onSave,
  onReset,
}: UserLimitModalProps) {
  const [formValues, setFormValues] = useState({ unpaid_annonce_limit: 0, unpaid_amount_limit: 0 });
  const [saving, setSaving] = useState(false);
  const [resetting, setResetting] = useState(false);
  const [usedAnnonces, setUsedAnnonces] = useState(0);
  const [usedAmount, setUsedAmount] = useState(0);

  useEffect(() => {
    if (isOpen && user) {
      setFormValues({
        unpaid_annonce_limit: user.unpaid_annonce_limit ?? 0,
        unpaid_amount_limit: user.unpaid_amount_limit ?? 0,
      });

      getClientWithLimits(user.id)
        .then((res) => {
          setUsedAnnonces(res.unpaid_annonces_count ?? 0);
          setUsedAmount(res.unpaid_amount ?? 0);
        })
        .catch((err) => {
          console.error("Erreur getClientWithLimits:", err);
          toast.error("Impossible de récupérer les valeurs utilisées");
        });
    }
  }, [isOpen, user]);

  if (!user) return null;

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormValues(prev => ({ ...prev, [name]: Number(value) }));
  };

  const handleSave = async () => {
    if (!user) return;
    setSaving(true);
    try {
      await onSave(user.id as number, formValues);
      toast.success("Plafond mis à jour !");

      const res = await getClientWithLimits(user.id);
      setUsedAnnonces(res.unpaid_annonces_count ?? 0);
      setUsedAmount(res.unpaid_amount ?? 0);

      onClose();
    } catch (err) {
      console.error(err);
      toast.error("Erreur lors de la mise à jour du plafond");
    } finally {
      setSaving(false);
    }
  };

  const handleReset = async () => {
    if (!user) return;
    if (!window.confirm("Voulez-vous vraiment réinitialiser ce compteur ?")) return;
    setResetting(true);
    try {
      await onReset(user.id as number);
      toast.success("Compteur réinitialisé !");
      setFormValues({
        unpaid_annonce_limit: user.unpaid_annonce_limit ?? 0,
        unpaid_amount_limit: user.unpaid_amount_limit ?? 0,
      });
      onClose();
    } catch (err) {
      console.error(err);
      toast.error("Erreur lors de la réinitialisation");
    } finally {
      setResetting(false);
    }
  };

  const remainingAnnonces = formValues.unpaid_annonce_limit - usedAnnonces;
  const remainingAmount = formValues.unpaid_amount_limit - usedAmount;

  const getProgressVariant = (used: number, total: number) => {
    const percentage = total > 0 ? (used / total) * 100 : 0;
    if (percentage >= 90) return "danger";
    if (percentage >= 70) return "warning";
    return "info";
  };

  const annoncesPercentage = formValues.unpaid_annonce_limit > 0 ? (usedAnnonces / formValues.unpaid_annonce_limit) * 100 : 0;
  const amountPercentage = formValues.unpaid_amount_limit > 0 ? (usedAmount / formValues.unpaid_amount_limit) * 100 : 0;

  return (
    <Modal show={isOpen} onHide={onClose} centered backdrop="static" size="lg"   style={{ zIndex: 9999 }}>
      <Modal.Header closeButton className="text-white" style={{ backgroundColor: '#4bb0c0' }}>
        <Modal.Title>
          <FaChartBar className="me-2" />
          Gestion des plafonds - {user.name || user.firstname}
        </Modal.Title>
      </Modal.Header>

      <Modal.Body>
        <Row className="mb-4">
          <Col md={6}>
            <Card className="h-100 shadow-sm border-0">
              <Card.Body>
                <Card.Title className="d-flex align-items-center">
                  <FaExclamationTriangle className="text-warning me-2" />
                  Annonces impayées
                </Card.Title>
                <div className="d-flex justify-content-between mb-2">
                  <span className="text-muted">Utilisées</span>
                  <strong>{usedAnnonces} / {formValues.unpaid_annonce_limit}</strong>
                </div>
                <ProgressBar 
                  now={annoncesPercentage} 
                  variant={getProgressVariant(usedAnnonces, formValues.unpaid_annonce_limit)}
                  className="mb-3"
                  animated={annoncesPercentage > 70}
                />
                <div className="d-flex justify-content-between">
                  <span className="text-muted">Restantes</span>
                  <strong className={remainingAnnonces < 0 ? "text-danger" : "text-success"}>
                    {remainingAnnonces}
                  </strong>
                </div>
              </Card.Body>
            </Card>
          </Col>
          
          <Col md={6}>
            <Card className="h-100 shadow-sm border-0">
              <Card.Body>
                <Card.Title className="d-flex align-items-center">
                  <FaMoneyBillWave className="text-danger me-2" />
                  Montant impayé
                </Card.Title>
                <div className="d-flex justify-content-between mb-2">
                  <span className="text-muted">Utilisé</span>
                  <strong>{usedAmount.toFixed(2)}€ / {formValues.unpaid_amount_limit}€</strong>
                </div>
                <ProgressBar 
                  now={amountPercentage} 
                  variant={getProgressVariant(usedAmount, formValues.unpaid_amount_limit)}
                  className="mb-3"
                  animated={amountPercentage > 70}
                />
                <div className="d-flex justify-content-between">
                  <span className="text-muted">Restant</span>
                  <strong className={remainingAmount < 0 ? "text-danger" : "text-success"}>
                    {remainingAmount.toFixed(2)}€
                  </strong>
                </div>
              </Card.Body>
            </Card>
          </Col>
        </Row>

        <Card className="shadow-sm">
          <Card.Header className="bg-light">
            <h6 className="mb-0">Configuration des plafonds</h6>
          </Card.Header>
          <Card.Body>
            <Row>
              <Col md={6}>
                <Form.Group className="mb-3" controlId="unpaid_annonce_limit">
                  <Form.Label>
                    <strong>Plafond d'annonces impayées</strong>
                  </Form.Label>
                  <Form.Control
                    type="number"
                    name="unpaid_annonce_limit"
                    value={formValues.unpaid_annonce_limit}
                    onChange={handleChange}
                    min={0}
                    className="border-primary"
                  />
                  <Form.Text className="text-muted">
                    Définir le nombre maximum d'annonces impayées autorisées
                  </Form.Text>
                </Form.Group>
              </Col>
              
              <Col md={6}>
                <Form.Group className="mb-3" controlId="unpaid_amount_limit">
                  <Form.Label>
                    <strong>Plafond de montant impayé (€)</strong>
                  </Form.Label>
                  <Form.Control
                    type="number"
                    name="unpaid_amount_limit"
                    value={formValues.unpaid_amount_limit}
                    onChange={handleChange}
                    min={0}
                    step="0.01"
                    className="border-primary"
                  />
                  <Form.Text className="text-muted">
                    Définir le montant maximum d'impayés autorisé
                  </Form.Text>
                </Form.Group>
              </Col>
            </Row>
          </Card.Body>
        </Card>
      </Modal.Body>

      <Modal.Footer className="d-flex justify-content-between border-top pt-3">
        <Button
          variant="outline-secondary"
          onClick={onClose}
          disabled={saving || resetting}
          className="px-4"
        >
          Annuler
        </Button>
        
        <div className="d-flex gap-2">
          {/* <Button
            style={{ backgroundColor: '#4bb0c0', borderColor: '#4bb0c0', color: 'white' }}
            onClick={handleReset}
            disabled={resetting || saving}
            className="d-flex align-items-center gap-2 px-4"
          >
            {resetting ? (
              <Spinner as="span" animation="border" size="sm" />
            ) : (
              <>
                <FaRedo />
                Réinitialiser
              </>
            )}
          </Button>
           */}
          <Button
            variant="primary"
            onClick={handleSave}
            disabled={saving || resetting}
            className="px-4"
          >
            {saving ? (
              <>
                <Spinner as="span" animation="border" size="sm" className="me-2" />
                Enregistrement...
              </>
            ) : (
              'Enregistrer les modifications'
            )}
          </Button>
        </div>
      </Modal.Footer>
    </Modal>
  );
}